import { useCallback, useState } from "react";
import { SingleSelectFormFieldConfig } from "@evergiving/common/dist/es2015/form";
import { RootState, VersionedStringFormState } from "@/lib/redux/types";
import { LabelledField } from "@/lib/components/LabelledField";
import { LabelText } from "@/lib/components/LabelText";
import { useAppDispatch, useAppSelector } from "@/lib/hooks/redux";
import { resetFormErrors, setFormValue } from "@/lib/redux/actions";
import { useFieldValidationErrors } from "@/lib/hooks/useFieldValidationErrors";
import { FieldError } from "@/lib/components/FieldError";
import { useShowErrors } from "@/lib/hooks/useShowErrors";

export function SingleSelectField({
  name,
  config,
  autofocus,
}: {
  name: keyof VersionedStringFormState;
  config: SingleSelectFormFieldConfig<string>;
  autofocus?: boolean;
}) {
  const required = !!config.requiredFieldErrorText;
  const valueFromStore = useAppSelector(
    (state: RootState) => state[name].value
  );
  const dispatch = useAppDispatch();
  const fieldValidationErrors = useFieldValidationErrors(config);
  const anyErrors = useShowErrors(config, name, valueFromStore || "");
  const [isDirty, setIsDirty] = useState(false);

  const handleBlur = useCallback(
    ({ target: { value } }: React.ChangeEvent<HTMLSelectElement>) => {
      if (required && (value === "" || value === undefined)) {
        dispatch(setFormValue(name, undefined, true));
      }

      setIsDirty(true);
    },
    [dispatch, name, required]
  );

  const handleChange = useCallback(
    ({ target: { value } }: React.ChangeEvent<HTMLSelectElement>) => {
      dispatch(
        setFormValue(
          name,
          value,
          fieldValidationErrors.forValue(value).length > 0
        )
      );
      dispatch(resetFormErrors(name));
    },
    [dispatch, fieldValidationErrors, name]
  );

  const errorId = `${name}-errors`;

  return (
    <LabelledField id={name}>
      <LabelText requiredIndicator={required}>{config.labelText}</LabelText>
      {/* eslint-disable jsx-a11y/no-autofocus */}
      {/* ^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^ Discussion of this override follows:

       We're only passing through the attribute here.  See DonationDetailsPage for
       a discussion why we would ever want to set this attribute.

       */}
      <select
        className="select-css"
        placeholder={config.placeholderText}
        value={valueFromStore || ""}
        onBlur={handleBlur}
        onChange={handleChange}
        aria-required={required}
        aria-invalid={(anyErrors && isDirty) || undefined}
        aria-describedby={anyErrors ? errorId : undefined}
        autoFocus={autofocus}
      >
        {/* eslint-enable jsx-a11y/no-autofocus */}
        <option value="" disabled hidden>
          {config.placeholderText}
        </option>
        {config.options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.labelText}
          </option>
        ))}
      </select>
      <div role="alert">
        {anyErrors && isDirty ? (
          <FieldError
            id={errorId}
            messages={fieldValidationErrors.forValue(valueFromStore)}
          />
        ) : null}
      </div>
    </LabelledField>
  );
}
